import { HTMLAttributes, useEffect, useMemo, useState } from 'preact/compat';
import {
  Container,
  ResolveImageUrlOptions,
  Slot,
  SlotProps,
} from '@adobe-commerce/elsie/lib';

import { Image, ImageProps } from '@adobe-commerce/elsie/components';
import { events } from '@adobe-commerce/event-bus';
import { ProductModel } from '@/pdp/data/models/product-model';
import { getFetchedProductData } from '@/pdp/api';
import { useText } from '@adobe-commerce/elsie/i18n';
import { Carousel, Overlay, Zoom } from '@/pdp/components';
import {
  isDirectVideoFile,
  getEmbedUrl,
  getVideoPreviewUrl,
  getVideoMimeType,
} from '@/pdp/lib/videoUtils';

type DefaultSlotContext = {
  data: ProductModel | null;
};

export interface ProductGalleryProps
  extends Omit<HTMLAttributes<HTMLDivElement>, 'controls'> {
  scope?: string;
  controls?: 'thumbnailsRow' | 'thumbnailsColumn' | 'dots' | null;
  loop?: boolean;
  peak?: boolean;
  gap?: 'small' | 'medium' | 'large' | null;
  arrows?: boolean;
  arrowsOnMainImage?: boolean;
  imageParams?: ResolveImageUrlOptions;
  thumbnailParams?: ResolveImageUrlOptions;
  zoom?: { closeButton: boolean } | boolean;
  videos?: boolean | { position: 'first' | 'last' };
  slots?: {
    CarouselThumbnail?: SlotProps<
      DefaultSlotContext & {
        defaultImageProps: ImageProps;
        mediaType?: 'image' | 'video';
        previewUrl?: string;
      }
    >;
    CarouselMainImage?: SlotProps<
      DefaultSlotContext & {
        defaultImageProps: ImageProps;
        mediaType?: 'image' | 'video';
        previewUrl?: string;
      }
    >;
  };
}

export const ProductGallery: Container<
  ProductGalleryProps,
  ProductModel | null
> = ({
  initialData = null,
  scope,
  controls = 'dots',
  loop = true,
  peak = false,
  gap = null,
  arrows = true,
  arrowsOnMainImage = false,
  zoom = false,
  videos = false,
  imageParams,
  thumbnailParams,
  children,
  slots,
  ...props
}) => {
  const ImagesLabel = useText('PDP.Product.Image.label').label;

  const [data, setData] = useState<ProductModel | null>(initialData);

  const [zoomTo, setZoomTo] = useState<number | null>(null);

  useEffect(() => {
    const dataEvent = events.on('pdp/data', setData, { scope });
    return () => {
      dataEvent?.off();
    };
  }, [scope]);

  const defaultSlotContext: DefaultSlotContext = useMemo(
    () => ({ data }),
    [data]
  );

  // Combine images and videos into a single media array based on videos prop
  const mediaItems = useMemo(() => {
    const imageItems =
      data?.images?.map((img) => ({
        type: 'image' as const,
        url: img.url,
        label: img.label,
      })) ?? [];

    if (!videos) {
      return imageItems;
    }

    const videoItems =
      data?.videos?.map((video) => ({
        type: 'video' as const,
        url: video.url,
        label: video.title ?? '',
        preview: video.preview,
      })) ?? [];

    const position = typeof videos === 'object' ? videos.position : 'last';

    return position === 'first'
      ? [...videoItems, ...imageItems]
      : [...imageItems, ...videoItems];
  }, [data?.images, data?.videos, videos]);

  const galleryItems = useMemo(
    () =>
      mediaItems.length > 0 ? (
        mediaItems.map((item, key) => {
          const Item = ({ ...itemProps }) => {
            const defaultProps = {
              title: item.label,
              src: item.url,
              alt: ImagesLabel?.replace('{product}', item.label)
                .replace('{key}', String(key + 1))
                .replace('{total}', String(mediaItems.length)),

              // TODO: change width/height for image aspect ratio
              // pending https://jira.corp.adobe.com/browse/DATA-5122
              width: imageParams?.width ?? 960,
              height: imageParams?.height ?? 1191,
              params: imageParams ?? { width: 960 },
              loading: key === 0 ? 'eager' : 'lazy',
              ...itemProps,
            } as const;

            const previewUrl =
              item.type === 'video'
                ? getVideoPreviewUrl(item.url, item.preview?.url)
                : undefined;

            return (
              <Slot
                name="CarouselMainImage"
                slot={slots?.CarouselMainImage}
                context={{
                  ...defaultSlotContext,
                  defaultImageProps: defaultProps,
                  mediaType: item.type,
                  previewUrl,
                }}
              >
                {item.type === 'video' ? (
                  isDirectVideoFile(item.url) ? (
                    <video
                      className="pdp-product-gallery__video"
                      controls
                      preload="metadata"
                      poster={previewUrl}
                      width={imageParams?.width ?? 960}
                      height={imageParams?.height ?? 1191}
                    >
                      <source
                        src={item.url}
                        type={getVideoMimeType(item.url)}
                      />
                      Your browser does not support the video tag.
                    </video>
                  ) : (
                    <iframe
                      className="pdp-product-gallery__video-iframe"
                      src={getEmbedUrl(item.url)}
                      title={item.label || 'Product video'}
                      width={imageParams?.width ?? 960}
                      height={imageParams?.height ?? 1191}
                      loading="lazy"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  )
                ) : (
                  <Image {...defaultProps} />
                )}
              </Slot>
            );
          };

          return zoom && item.type === 'image' ? (
            <Zoom
              key={item.url}
              closeButton={typeof zoom === 'object' ? zoom.closeButton : false}
            >
              <Item />
            </Zoom>
          ) : (
            <Item key={item.url} onClick={() => setZoomTo(key)} />
          );
        })
      ) : (
        <img
          key="pleaceholder"
          src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
          className="pdp-product-gallery__images__placeholder"
          width={imageParams?.width ?? 960}
          height={imageParams?.height ?? 1191}
          aria-hidden
        />
      ),
    [
      ImagesLabel,
      mediaItems,
      imageParams,
      zoom,
      defaultSlotContext,
      slots?.CarouselMainImage,
    ]
  );

  const thumbnails = useMemo(() => {
    return mediaItems.length > 0 ? (
      mediaItems.map((item, key) => {
        const defaultProps = {
          title: item.label,
          src: item.url,
          alt: ImagesLabel?.replace('{product}', item.label)
            .replace('{key}', String(key + 1))
            .replace('{total}', String(mediaItems.length)),

          // TODO: change width/height for image aspect ratio
          // pending https://jira.corp.adobe.com/browse/DATA-5122
          width: thumbnailParams?.width ?? 200,
          height: thumbnailParams?.height ?? 248,
          params: thumbnailParams ?? { width: 200 },
          loading: 'lazy',
        } as const;

        const previewUrl =
          item.type === 'video'
            ? getVideoPreviewUrl(item.url, item.preview?.url)
            : undefined;

        return (
          <Slot
            key={item.url}
            name="CarouselThumbnail"
            slotTag="span"
            contentTag="span"
            slot={slots?.CarouselThumbnail}
            context={{
              ...defaultSlotContext,
              defaultImageProps: defaultProps,
              mediaType: item.type,
              previewUrl,
            }}
          >
            {item.type === 'video' ? (
              <span
                className="pdp-product-gallery__video-thumbnail"
                role="img"
                aria-label={`Play video: ${item.label || 'Product video'}`}
              >
                {previewUrl ? (
                  <img
                    src={previewUrl}
                    alt=""
                    width={thumbnailParams?.width ?? 200}
                    height={thumbnailParams?.height ?? 248}
                    loading="lazy"
                    aria-hidden="true"
                  />
                ) : (
                  <span
                    className="pdp-product-gallery__video-thumbnail-placeholder"
                    style={{
                      width: thumbnailParams?.width ?? 200,
                      height: thumbnailParams?.height ?? 248,
                    }}
                    aria-hidden="true"
                  />
                )}
                <span
                  className="pdp-product-gallery__video-icon"
                  aria-hidden="true"
                >
                  ▶
                </span>
              </span>
            ) : (
              <Image {...defaultProps} />
            )}
          </Slot>
        );
      })
    ) : (
      <img
        key="pleaceholder"
        src="data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7"
        className="pdp-product-gallery__thumbnail__placeholder"
        width={thumbnailParams?.width ?? 200}
        height={thumbnailParams?.height ?? 248}
        aria-hidden
      />
    );
  }, [
    ImagesLabel,
    mediaItems,
    thumbnailParams,
    defaultSlotContext,
    slots?.CarouselThumbnail,
  ]);

  if (!data) return null;

  return (
    <div {...props}>
      {/* Carousel */}
      <Carousel
        arrows={!!mediaItems.length && arrows}
        arrowsOnMainImage={arrowsOnMainImage}
        controls={mediaItems.length ? controls : undefined}
        loop={loop && !!mediaItems.length}
        peak={!!mediaItems.length && peak}
        gap={gap}
        defaultIndex={zoomTo || 0}
        thumbnails={thumbnails}
      >
        {galleryItems}
      </Carousel>

      {/* Zoom Overlay */}
      {zoomTo === null ? null : (
        <Overlay onClose={() => setZoomTo(null)} centered>
          <Carousel
            arrows={!!mediaItems.length}
            width="100%"
            height="100%"
            defaultIndex={zoomTo}
            loop={loop && !!mediaItems.length}
            controls="dots"
            isZoomed
          >
            {galleryItems}
          </Carousel>
        </Overlay>
      )}
    </div>
  );
};

ProductGallery.getInitialData = getFetchedProductData;
